/**
 * Standards Preview Component - Right Panel
 * Displays preview standards that would be aligned based on current tag configuration
 */

import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from '@mui/icons-material';
import { TagCandidate, Standard, MathExpression, CANNED_GRADES, CANNED_JURISDICTIONS, CANNED_STATES, CANNED_DOCUMENTS } from '../../types';
import { standardsApi, filterStandards } from '../../services/spine.service';
import { usePreferences } from '../../hooks/usePreferences';
import { createStandardsKey, buildPreviewRequests } from '../../utils/standardsPreview';

// Helper functions for Select renderValue - moved outside component to prevent re-creation
const renderChipValues = (selected: string[]) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
    {selected.map(item => (
      <Chip key={item} label={item} size="small" />
    ))}
  </Box>
);


// Collapsible Section Component (matching Streamlit pattern)
interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

function CollapsibleSection({ title, children, defaultExpanded = true }: CollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={{ mb: 2 }}>
      <Button
        onClick={() => setExpanded(!expanded)}
        sx={{
          textTransform: 'none',
          mb: 1,
          color: 'text.primary',
          fontSize: '0.9rem',
          fontWeight: 'bold',
          p: 0,
          minWidth: 'auto',
          justifyContent: 'flex-start',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}
      >
        {expanded ? '▼' : '▶'} {title}
      </Button>
      {expanded && (
        <Box sx={{ ml: 2 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

interface StandardsPreviewProps {
  tagCandidates: TagCandidate[];
  mathExpressions?: MathExpression[];
  selectedGrades: string[];
  selectedDocuments: string[];
  selectedJurisdictions: string[];
  selectedStates: string[];
  showNonSelectedTags: boolean;
  searchText: string;
  onGradesChange: (grades: string[]) => void;
  onDocumentsChange: (documents: string[]) => void;
  onJurisdictionsChange: (jurisdictions: string[]) => void;
  onStatesChange: (states: string[]) => void;
  onShowNonSelectedTagsChange: (show: boolean) => void;
  onSearchTextChange: (searchText: string) => void;
  onStandardClick?: (standardId: string | null) => void;
  focusedStandardId?: string | null;
  focusedTagStandardIds?: string[];
}

export default function StandardsPreview({
  tagCandidates,
  mathExpressions,
  selectedGrades,
  selectedDocuments,
  selectedJurisdictions,
  selectedStates,
  showNonSelectedTags,
  searchText,
  onGradesChange,
  onDocumentsChange,
  onJurisdictionsChange,
  onStatesChange,
  onShowNonSelectedTagsChange,
  onSearchTextChange,
  onStandardClick,
  focusedStandardId,
  focusedTagStandardIds,
}: StandardsPreviewProps) {
  const { preferences, save } = usePreferences();

  // Local state for immediate UI feedback
  const [localSearchText, setLocalSearchText] = useState(searchText);

  // Debounced search handler that updates parent state
  const debouncedSearchChange = useCallback(
    useMemo(() => {
      let timeoutId: number;
      return (value: string) => {
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          onSearchTextChange(value);
        }, 300); // 300ms debounce
      };
    }, [onSearchTextChange]),
    [onSearchTextChange]
  );

  // Handle input change - update local state immediately and debounce parent update
  const handleSearchChange = useCallback((value: string) => {
    setLocalSearchText(value); // Immediate UI update
    debouncedSearchChange(value); // Debounced filtering
  }, [debouncedSearchChange]);

  // Sync local state when parent searchText changes (e.g., on clear filters)
  useMemo(() => {
    setLocalSearchText(searchText);
  }, [searchText]);

  // Map tab names to indices
  const tabNameToIndex = { 'filters': 0, 'metrics': 1, 'standards': 2 };
  const tabIndexToName = ['filters', 'metrics', 'standards'] as const;

  // Initialize active tab from preferences
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = preferences.standardsPreviewTab;
    return tabNameToIndex[savedTab as keyof typeof tabNameToIndex] ?? 1; // Default to metrics (index 1)
  });

  // Handle tab change and save preference
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const tabName = tabIndexToName[newValue];
    if (tabName) {
      save({ standardsPreviewTab: tabName });
    }
  };

  // Filters state is now managed by parent component

  // Use shared utility for cache keys and request building
  const selectedTagsKey = useMemo(
    () => createStandardsKey(tagCandidates, true),
    [tagCandidates]
  );

  const selectedTags = useMemo(
    () => buildPreviewRequests(tagCandidates, mathExpressions, true),
    [tagCandidates, mathExpressions]
  );

  // Fetch standards for selected tags only
  // Return shape matches BipartiteChart for cache sharing
  const { data: standardsDataRaw, isLoading, error } = useQuery({
    queryKey: ['standards-preview-selected', selectedTagsKey],
    queryFn: async () => {
      if (selectedTags.length === 0) {
        return { standards: [] as Standard[], tagToStandardsMap: new Map<string, string[]>() };
      }

      // Batch fetch standards for all selected tags (already normalized by the service)
      const results = await standardsApi.batchPreview(selectedTags);
      const tagToStandardsMap = new Map<string, string[]>();
      const uniqueStandards = new Map<string, Standard>();

      Object.entries(results).forEach(([key, standards]) => {
        const standardIds: string[] = [];
        standards.forEach((std) => {
          standardIds.push(std.standardId);
          if (!uniqueStandards.has(std.standardId)) {
            uniqueStandards.set(std.standardId, std);
          }
        });
        tagToStandardsMap.set(key, standardIds);
      });

      return {
        standards: Array.from(uniqueStandards.values()),
        tagToStandardsMap,
      };
    },
    enabled: selectedTags.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract standards arrays for backward compatibility with existing code
  const standardsData = standardsDataRaw?.standards;

  // Apply filters to standards data
  const filteredStandardsData = useMemo(() => {
    return filterStandards(standardsData || [], {
      grades: selectedGrades,
      documents: selectedDocuments,
      jurisdictions: selectedJurisdictions,
      states: selectedStates,
    });
  }, [standardsData, selectedGrades, selectedDocuments, selectedJurisdictions, selectedStates]);

  // Calculate metrics for all selected competencies (unfiltered - for "All Alignments" view)
  const allMetrics = useMemo(() => {
    if (!standardsData || standardsData.length === 0) {
      return null;
    }

    const allGrades = new Set<string>();
    let nationalCount = 0;
    let stateCount = 0;
    const statesCounts = new Map<string, number>();
    const documentCodeCounts = new Map<string, number>();

    standardsData.forEach((std) => {
      if (std.grade) allGrades.add(std.grade);
      if (std.jurisdiction === 'NATIONAL') nationalCount++;
      if (std.jurisdiction === 'STATE') stateCount++;
      if (std.state) {
        statesCounts.set(std.state, (statesCounts.get(std.state) || 0) + 1);
      }
      if (std.document) {
        documentCodeCounts.set(std.document, (documentCodeCounts.get(std.document) || 0) + 1);
      }
    });

    // Sort grades in ascending order (K, 1, 2, ..., 10, 11, 12)
    const sortedGrades = Array.from(allGrades).sort((a, b) => {
      // Handle Kindergarten
      if (a === 'K' || a.toLowerCase() === 'kindergarten') return -1;
      if (b === 'K' || b.toLowerCase() === 'kindergarten') return 1;

      // Parse numeric grades
      const numA = parseInt(a);
      const numB = parseInt(b);

      // Both are numbers - ascending order
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      // Fallback to alphabetical for non-numeric
      return a.localeCompare(b);
    });

    // Create comprehensive states list with all 50 states (like Streamlit)
    const allStatesWithCounts = CANNED_STATES.map(state => [
      state,
      statesCounts.get(state) || 0
    ]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))); // Alphabetical sort (matching Streamlit)

    // Sort document codes alphabetically (matching Streamlit behavior)
    const documentCodesWithCounts = Array.from(documentCodeCounts.entries())
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])));

    // Calculate grade counts (0-12, matching Streamlit implementation)
    const gradeCounts = new Map<number, number>();
    for (let i = 0; i <= 12; i++) {
      gradeCounts.set(i, 0);
    }

    standardsData.forEach((std) => {
      if (std.grade) {
        // Convert grade label to integer (matching Streamlit's grade_to_int function)
        let gradeInt: number | null = null;
        if (std.grade === 'K' || std.grade.toLowerCase() === 'kindergarten') {
          gradeInt = 0;
        } else {
          const parsed = parseInt(std.grade);
          if (!isNaN(parsed) && parsed >= 0 && parsed <= 12) {
            gradeInt = parsed;
          }
        }

        if (gradeInt !== null) {
          gradeCounts.set(gradeInt, (gradeCounts.get(gradeInt) || 0) + 1);
        }
      }
    });

    const gradesWithCounts = Array.from(gradeCounts.entries())
      .map(([grade, count]) => [
        grade === 0 ? '0 (K)' : grade.toString(),
        count
      ]);

    // Calculate jurisdiction counts (including null values)
    const jurisdictionCounts = new Map<string, number>();
    standardsData.forEach((std) => {
      const jurisdiction = std.jurisdiction || 'null';
      jurisdictionCounts.set(
        jurisdiction,
        (jurisdictionCounts.get(jurisdiction) || 0) + 1
      );
    });

    const jurisdictionsWithCounts = Array.from(jurisdictionCounts.entries())
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])));

    return {
      totalStandards: standardsData.length,
      grades: sortedGrades,
      nationalCount,
      stateCount,
      statesCounts: Array.from(statesCounts.entries()).sort((a, b) => b[1] - a[1]), // Keep original for Settings tab
      allStatesWithCounts, // New comprehensive states list for Metrics tab
      documents: Array.from(documentCodeCounts.keys()).sort(), // Keep original for Settings tab
      documentCodesWithCounts, // New document codes with counts for Metrics tab
      gradesWithCounts, // New grades with counts for Metrics tab
      jurisdictions: Array.from(new Set(standardsData.map(std => std.jurisdiction).filter(Boolean))).sort(),
      jurisdictionsWithCounts, // New jurisdictions with counts for Metrics tab
    };
  }, [standardsData]);

  // Calculate metrics for filtered data
  const filteredMetrics = useMemo(() => {
    if (filteredStandardsData.length === 0) {
      return null;
    }

    const allGrades = new Set<string>();
    let nationalCount = 0;
    let stateCount = 0;
    const statesCounts = new Map<string, number>();
    const documentCodeCounts = new Map<string, number>();

    filteredStandardsData.forEach((std) => {
      if (std.grade) allGrades.add(std.grade);
      if (std.jurisdiction === 'NATIONAL') nationalCount++;
      if (std.jurisdiction === 'STATE') stateCount++;
      if (std.state) {
        statesCounts.set(std.state, (statesCounts.get(std.state) || 0) + 1);
      }
      if (std.document) {
        documentCodeCounts.set(std.document, (documentCodeCounts.get(std.document) || 0) + 1);
      }
    });

    // Sort grades in ascending order (K, 1, 2, ..., 10, 11, 12)
    const sortedGrades = Array.from(allGrades).sort((a, b) => {
      // Handle Kindergarten
      if (a === 'K' || a.toLowerCase() === 'kindergarten') return -1;
      if (b === 'K' || b.toLowerCase() === 'kindergarten') return 1;

      // Parse numeric grades
      const numA = parseInt(a);
      const numB = parseInt(b);

      // Both are numbers - ascending order
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      // Fallback to alphabetical for non-numeric
      return a.localeCompare(b);
    });

    // Create comprehensive states list with all 50 states (like Streamlit)
    const allStatesWithCounts = CANNED_STATES.map(state => [
      state,
      statesCounts.get(state) || 0
    ]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))); // Alphabetical sort (matching Streamlit)

    // Sort document codes alphabetically (matching Streamlit behavior)
    const documentCodesWithCounts = Array.from(documentCodeCounts.entries())
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])));

    // Calculate grade counts (0-12, matching Streamlit implementation)
    const gradeCounts = new Map<number, number>();
    for (let i = 0; i <= 12; i++) {
      gradeCounts.set(i, 0);
    }

    filteredStandardsData.forEach((std) => {
      if (std.grade) {
        // Convert grade label to integer (matching Streamlit's grade_to_int function)
        let gradeInt: number | null = null;
        if (std.grade === 'K' || std.grade.toLowerCase() === 'kindergarten') {
          gradeInt = 0;
        } else {
          const parsed = parseInt(std.grade);
          if (!isNaN(parsed) && parsed >= 0 && parsed <= 12) {
            gradeInt = parsed;
          }
        }

        if (gradeInt !== null) {
          gradeCounts.set(gradeInt, (gradeCounts.get(gradeInt) || 0) + 1);
        }
      }
    });

    const gradesWithCounts = Array.from(gradeCounts.entries())
      .map(([grade, count]) => [
        grade === 0 ? '0 (K)' : grade.toString(),
        count
      ]);

    // Calculate jurisdiction counts (including null values)
    const jurisdictionCounts = new Map<string, number>();
    filteredStandardsData.forEach((std) => {
      const jurisdiction = std.jurisdiction || 'null';
      jurisdictionCounts.set(
        jurisdiction,
        (jurisdictionCounts.get(jurisdiction) || 0) + 1
      );
    });

    const jurisdictionsWithCounts = Array.from(jurisdictionCounts.entries())
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])));

    return {
      totalStandards: filteredStandardsData.length,
      grades: sortedGrades,
      nationalCount,
      stateCount,
      statesCounts: Array.from(statesCounts.entries()).sort((a, b) => b[1] - a[1]), // Keep original for Settings tab
      allStatesWithCounts, // New comprehensive states list for Metrics tab
      documents: Array.from(documentCodeCounts.keys()).sort(), // Keep original for Settings tab
      documentCodesWithCounts, // New document codes with counts for Metrics tab
      gradesWithCounts, // New grades with counts for Metrics tab
      jurisdictions: Array.from(new Set(filteredStandardsData.map(std => std.jurisdiction).filter(Boolean))).sort(),
      jurisdictionsWithCounts, // New jurisdictions with counts for Metrics tab
    };
  }, [filteredStandardsData]);

  // Search filtering for standards tab - moved to top level to comply with Rules of Hooks
  const searchFilteredStandards = useMemo(() => {
    // Get data based on toggle selection
    const getBaseData = () => {
      switch (preferences.standardsViewMode) {
        case 'aligned':
        case 'all': // 'all' now behaves same as 'aligned' (canned filters used)
          return standardsData || [];
        case 'filtered-aligned':
          return filteredStandardsData || [];
        default:
          return standardsData || [];
      }
    };

    const baseData = getBaseData();

    // Apply search filter
    const filtered = searchText.trim()
      ? baseData.filter(standard =>
          standard.code?.toLowerCase().includes(searchText.toLowerCase()) ||
          standard.description?.toLowerCase().includes(searchText.toLowerCase())
        )
      : baseData;

    // Sort by code in ascending alphanumeric order
    return filtered.sort((a, b) => {
      const codeA = a.code || '';
      const codeB = b.code || '';
      return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [preferences.standardsViewMode, standardsData, filteredStandardsData, searchText]);


  const renderMetricsTab = () => {
    // Choose data based on toggle
    const isViewingTotal = preferences.metricsViewMode === 'all-alignments';
    const metrics = isViewingTotal ? allMetrics : (filteredMetrics || allMetrics);

    const handleMetricsViewChange = (
      _event: React.MouseEvent<HTMLElement>,
      newView: 'all-alignments' | 'filtered'
    ) => {
      if (newView !== null) {
        save({ metricsViewMode: newView });
      }
    };

    return (
      <Box>
        {/* Metrics View Toggle */}
        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={preferences.metricsViewMode}
            exclusive
            onChange={handleMetricsViewChange}
            size="small"
            sx={{ width: '100%' }}
          >
            <ToggleButton value="all-alignments" sx={{ flex: 1 }}>
              Alignments
            </ToggleButton>
            <ToggleButton value="filtered" sx={{ flex: 1 }}>
              Filtered Alignments
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            {isViewingTotal ? 'Total Standards' : `Filtered Standards${filteredMetrics && allMetrics ? ` (${filteredStandardsData.length} of ${allMetrics?.totalStandards})` : ''}`}
          </Typography>
          <Typography variant="h4">{metrics?.totalStandards ?? 0}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Jurisdiction
          </Typography>
          <TableContainer sx={{ mt: 0.5 }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0, py: 0.25, px: 0 } }}>
              <TableBody>
                {(metrics?.jurisdictionsWithCounts ?? []).map(([jurisdiction, count]) => (
                  <TableRow key={jurisdiction}>
                    <TableCell sx={{ fontWeight: 'bold', color: count === 0 ? 'text.disabled' : 'text.primary' }}>
                      <Typography variant="body2">
                        {jurisdiction === 'null' ? 'N/A' : jurisdiction}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: count === 0 ? 'text.disabled' : 'text.primary', width: '60px' }}>
                      <Typography variant="body2">
                        {count}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Grades
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {(metrics?.grades ?? []).map((grade) => (
              <Chip key={grade} label={grade} size="small" />
            ))}
          </Box>
        </Box>

        {metrics?.allStatesWithCounts && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary">
              {metrics.allStatesWithCounts.filter(([_, count]) => Number(count) > 0).length} states with standards
            </Typography>
            <CollapsibleSection
              title="Stds by State"
              defaultExpanded={false}
            >
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0, py: 0.25, px: 0 } }}>
                <TableBody>
                  {metrics.allStatesWithCounts.map(([state, count]) => (
                    <TableRow key={state}>
                      <TableCell sx={{ fontWeight: 'bold', color: count === 0 ? 'text.disabled' : 'text.primary' }}>
                        <Typography variant="body2">{state}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: count === 0 ? 'text.disabled' : 'text.primary', width: '60px' }}>
                        <Typography variant="body2">
                          {count}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CollapsibleSection>
          </Box>
        )}

        {metrics?.gradesWithCounts && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary">
              {metrics.gradesWithCounts.filter(([_, count]) => Number(count) > 0).length} grades
            </Typography>
            <CollapsibleSection
              title="Stds by Grade"
              defaultExpanded={false}
            >
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0, py: 0.25, px: 0 } }}>
                <TableBody>
                  {metrics.gradesWithCounts.map(([grade, count]) => (
                    <TableRow key={grade}>
                      <TableCell sx={{ fontWeight: 'bold', color: count === 0 ? 'text.disabled' : 'text.primary' }}>
                        <Typography variant="body2">{grade}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: count === 0 ? 'text.disabled' : 'text.primary', width: '60px' }}>
                        <Typography variant="body2">
                          {count}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CollapsibleSection>
          </Box>
        )}

        {metrics?.documentCodesWithCounts && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary">
              {metrics.documentCodesWithCounts.length} documents
            </Typography>
            <CollapsibleSection
              title="Stds by Document Code"
              defaultExpanded={false}
            >
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { border: 0, py: 0.25, px: 0 } }}>
                <TableBody>
                  {metrics.documentCodesWithCounts.map(([code, count]) => (
                    <TableRow key={code}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        <Typography variant="body2">{code}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: 'text.primary', width: '60px' }}>
                        <Typography variant="body2">{count}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CollapsibleSection>
          </Box>
        )}

      </Box>
    );
  };

  const renderStandardsTab = () => {
    // Use the top-level search filtered data
    const searchFilteredData = searchFilteredStandards;


    // Get base data count for display
    const getBaseDataCount = () => {
      switch (preferences.standardsViewMode) {
        case 'aligned':
        case 'all': // 'all' now behaves same as 'aligned' (canned filters used)
          return standardsData?.length || 0;
        case 'filtered-aligned':
          return filteredStandardsData?.length || 0;
        default:
          return standardsData?.length || 0;
      }
    };

    const baseDataCount = getBaseDataCount();

    // Helper function to determine standard color
    const getStandardColor = (standard: Standard) => {
      const isStandardFocused = focusedStandardId === standard.standardId;
      const isFromFocusedTag = focusedTagStandardIds?.includes(standard.standardId);
      const isFromSelectedCompetency = standardsData?.some(s => s.standardId === standard.standardId);

      // Yellow: Related to focused TAG (highest priority)
      if (isFromFocusedTag) {
        return '#FFC107'; // Yellow
      }

      // Green: Standard is directly focused (clicked on)
      if (isStandardFocused) {
        return '#4CAF50'; // Green
      }

      // Blue: Related to selected competency (but not focused tag or focused standard)
      if (isFromSelectedCompetency) {
        return '#2196F3'; // Blue
      }

      // Grey: Default
      return '#9E9E9E'; // Grey
    };

    const handleStandardsViewChange = (
      _event: React.MouseEvent<HTMLElement>,
      newView: 'aligned' | 'filtered-aligned' | 'all'
    ) => {
      if (newView !== null) {
        save({ standardsViewMode: newView });
      }
    };

    return (
      <Box>
        {/* Standards View Toggle */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
            Standards View
          </Typography>
          <ToggleButtonGroup
            value={preferences.standardsViewMode}
            exclusive
            onChange={handleStandardsViewChange}
            size="small"
            sx={{ width: '100%', mb: 2 }}
          >
            <ToggleButton value="aligned" sx={{ flex: 1 }}>
              Aligned
            </ToggleButton>
            <ToggleButton value="filtered-aligned" sx={{ flex: 1 }}>
              Filtered Aligned
            </ToggleButton>
            <ToggleButton value="all" sx={{ flex: 1 }}>
              All
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search standards by code or description..."
          value={localSearchText}
          onChange={(e) => handleSearchChange(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Results count */}
        <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
          Showing {searchFilteredData.length} of {baseDataCount} standards
        </Typography>

        {/* Standards list */}
        {searchFilteredData.map((standard) => {
          const standardColor = getStandardColor(standard);
          const isCurrentlyFocused = focusedStandardId === standard.standardId;

          const handleStandardClick = () => {
            if (onStandardClick) {
              // Toggle selection - if already focused, unfocus by passing null
              onStandardClick(isCurrentlyFocused ? null : standard.standardId);
            }
          };

          return (
            <Card
              key={standard.standardId}
              onClick={handleStandardClick}
              sx={{
                mb: 0.25,
                borderLeft: `4px solid ${standardColor}`,
                cursor: onStandardClick ? 'pointer' : 'default',
                '&:hover': {
                  bgcolor: onStandardClick ? 'action.hover' : 'transparent',
                },
              }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0 }}>
                  {standard.code}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {standard.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                  {standard.grade && (
                    <Chip label={`Grade ${standard.grade}`} size="small" />
                  )}
                  {standard.subject && (
                    <Chip label={standard.subject} size="small" />
                  )}
                  {standard.document && (
                    <Chip label={standard.document} size="small" />
                  )}
                  {standard.jurisdiction && (
                    <Chip label={standard.jurisdiction} size="small" />
                  )}
                  {standard.state && (
                    <Chip label={standard.state} size="small" />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.5,
                    color: 'text.disabled',
                    fontFamily: 'monospace',
                    fontSize: '0.65rem',
                    textAlign: 'right',
                  }}
                >
                  {standard.standardId}
                </Typography>
              </CardContent>
            </Card>
          );
        })}

        {searchFilteredData.length === 0 && searchText && (
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
            No standards match "{searchText}"
          </Typography>
        )}
      </Box>
    );
  };

  const renderSettingsTab = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {/* Chart Settings */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Chart Settings
          </Typography>
          <FormControl component="fieldset">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={showNonSelectedTags}
                onChange={(e) => onShowNonSelectedTagsChange(e.target.checked)}
                size="small"
              />
              <Typography variant="body2">
                Show non-selected tags in chart
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ ml: 4, mt: -0.5 }}>
              Display all tags in the chart, including those with unselected competencies
            </Typography>
          </FormControl>
        </Box>

        {/* Divider */}
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 1 }} />

        {/* Standards Filters */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Standards Filters
        </Typography>

        {/* Search Filter */}
        <TextField
          size="small"
          placeholder="Filter by code or description..."
          value={localSearchText}
          onChange={(e) => handleSearchChange(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Grade Filter */}
        <FormControl size="small" fullWidth>
          <InputLabel>Grades</InputLabel>
          <Select
            multiple
            value={selectedGrades}
            onChange={(e) => onGradesChange(e.target.value as string[])}
            input={<OutlinedInput label="Grades" />}
            renderValue={renderChipValues}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 600,
                },
              },
              MenuListProps: {
                dense: true,
                disablePadding: true,
              },
            }}
          >
            {CANNED_GRADES.map((grade) => (
              <MenuItem key={grade} value={grade} dense sx={{ py: 0.5, minHeight: 32 }}>
                <Checkbox checked={selectedGrades.includes(grade)} size="small" sx={{ p: 0.5 }} />
                <ListItemText primary={grade} sx={{ my: 0 }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Document Filter - Searchable Multi-select Autocomplete */}
        <Autocomplete
          multiple
          size="small"
          options={CANNED_DOCUMENTS}
          value={selectedDocuments}
          onChange={(_, newValue) => onDocumentsChange(newValue)}
          renderInput={(params) => (
            <TextField {...params} label="Documents" placeholder="Search documents..." />
          )}
          ListboxProps={{
            style: { maxHeight: 400 },
          }}
          limitTags={2}
          ChipProps={{ size: 'small' }}
        />

        {/* Jurisdiction Filter */}
        <FormControl size="small" fullWidth>
          <InputLabel>Jurisdiction</InputLabel>
          <Select
            multiple
            value={selectedJurisdictions}
            onChange={(e) => onJurisdictionsChange(e.target.value as string[])}
            input={<OutlinedInput label="Jurisdiction" />}
            renderValue={renderChipValues}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 600,
                },
              },
              MenuListProps: {
                dense: true,
                disablePadding: true,
              },
            }}
          >
            {CANNED_JURISDICTIONS.map((jurisdiction) => (
              <MenuItem key={jurisdiction} value={jurisdiction} dense sx={{ py: 0.5, minHeight: 32 }}>
                <Checkbox checked={selectedJurisdictions.includes(jurisdiction)} size="small" sx={{ p: 0.5 }} />
                <ListItemText primary={jurisdiction} sx={{ my: 0 }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* State Filter */}
        <FormControl size="small" fullWidth>
          <InputLabel>States</InputLabel>
          <Select
            multiple
            value={selectedStates}
            onChange={(e) => onStatesChange(e.target.value as string[])}
            input={<OutlinedInput label="States" />}
            renderValue={renderChipValues}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 600,
                },
              },
              MenuListProps: {
                dense: true,
                disablePadding: true,
              },
            }}
          >
            {CANNED_STATES.map((state) => (
              <MenuItem key={state} value={state} dense sx={{ py: 0.5, minHeight: 32 }}>
                <Checkbox checked={selectedStates.includes(state)} size="small" sx={{ p: 0.5 }} />
                <ListItemText primary={state} sx={{ my: 0 }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Clear Filters Button */}
        <Box sx={{ pt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              onGradesChange([]);
              onDocumentsChange([]);
              onJurisdictionsChange([]);
              onStatesChange([]);
              onSearchTextChange('');
            }}
            disabled={
              selectedGrades.length === 0 &&
              selectedDocuments.length === 0 &&
              selectedJurisdictions.length === 0 &&
              selectedStates.length === 0 &&
              searchText.trim() === ''
            }
          >
            Clear All Filters
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6">
          Standards
        </Typography>
        {isLoading && (
          <CircularProgress size={18} sx={{ color: '#4CAF50' }} />
        )}
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Filters
              {(selectedGrades.length > 0 ||
                selectedDocuments.length > 0 ||
                selectedJurisdictions.length > 0 ||
                selectedStates.length > 0 ||
                searchText.trim() !== '') && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    ml: 0.5
                  }}
                />
              )}
            </Box>
          }
        />
        <Tab label="Metrics" />
        <Tab label="Standards" />
      </Tabs>

      {/* Tab Content */}
      <Box sx={{ height: 'calc(100% - 140px)', overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load standards: {error?.message}
          </Alert>
        )}

        {activeTab === 0 && renderSettingsTab()}
        {activeTab === 1 && renderMetricsTab()}
        {activeTab === 2 && renderStandardsTab()}
      </Box>
    </Box>
  );
}
